import { FormEvent, useRef, useState } from "react"
import { Copy, Minus, Plus } from "lucide-react"
import toast from "react-hot-toast"
import { BANK, SITE } from "@/data/site"
import { lumi } from "@/lib/lumi"

const SYNC_ENDPOINT =
  "https://script.google.com/macros/s/AKfycbx0G2xfvzSGlSekIirIxUv9aV5hZbkU4XYJyynRdQVBULY1WfgIkMImyVI_wPChit2M/exec"

// 同步一份資料到外部試算表；失敗不影響站內訂單成立
async function syncOrder(payload: Record<string, unknown>) {
  try {
    await fetch(SYNC_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      body: JSON.stringify(payload),
    })
  } catch (error) {
    console.warn("外部同步失敗：", error)
  }
}

const inputClass =
  "w-full border-b border-white/15 bg-transparent pb-2.5 font-serif-tc text-base text-stone-100 outline-none transition-colors duration-300 placeholder:text-stone-600 focus:border-white"

function SectionTitle({ index, children }: { index: string; children: React.ReactNode }) {
  return (
    <div className="mt-16 first:mt-0">
      <div className="flex items-baseline gap-4">
        <span className="font-sans-ui text-[10px] tracking-[0.3em] text-white/45">{index}</span>
        <h3 className="font-sans-ui text-sm font-bold tracking-[0.24em] text-white">{children}</h3>
      </div>
      <div className="mt-4 h-px w-full bg-gradient-to-r from-white/25 via-white/10 to-transparent" />
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-3">
      <span className="font-sans-ui text-[11px] tracking-[0.22em] text-stone-400">{label}</span>
      {children}
    </label>
  )
}

function Order() {
  const formRef = useRef<HTMLFormElement>(null)
  const [qty, setQty] = useState(1)
  const [copied, setCopied] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const orderTotal = qty * SITE.price

  const copyValue = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(label)
      window.setTimeout(() => setCopied(""), 1600)
    } catch {
      toast.error("複製失敗，請手動複製")
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submitting) return
    if (qty < 1) {
      toast.error("請選擇購買數量")
      return
    }

    const form = event.currentTarget
    const formData = new FormData(form)
    const name = String(formData.get("name") || "")
    const phone = String(formData.get("phone") || "")
    const email = String(formData.get("email") || "")
    const transferDate = String(formData.get("transferDate") || "")
    const transferAmount = String(formData.get("transferAmount") || "")
    const accountLastFive = String(formData.get("accountLastFive") || "")
    const storeName = String(formData.get("storeName") || "")
    const storeAddress = String(formData.get("storeAddress") || "")
    const note = String(formData.get("note") || "")

    const now = new Date().toISOString()
    const transferAmountNumber = Number(transferAmount) || 0

    const orderPayload = {
      name,
      phone,
      email,
      product: SITE.productName,
      edition: SITE.edition,
      quantity: qty,
      price: SITE.price,
      amount: orderTotal,
      transferDate,
      transferAmount: transferAmountNumber,
      accountLastFive,
      receiverName: name,
      receiverPhone: phone,
      storeName,
      storeAddress,
      note,
      status: "pending",
      creator: email || name,
      createdAt: now,
      updatedAt: now,
    }

    setSubmitting(true)
    try {
      await lumi.entities.orders.create(orderPayload)
      void syncOrder(orderPayload)
      form.reset()
      setQty(1)
      toast.success("送出成功")
    } catch (error) {
      console.error("訂單送出失敗：", error)
      toast.error("送出失敗，請稍後再試")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section id="order" className="relative w-full bg-black px-6 py-24 md:px-16 md:py-32 lg:px-24">
      <div className="mx-auto max-w-2xl">
        <div className="text-center">
          <p className="font-sans-ui text-[10px] tracking-[0.48em] text-stone-400">Order</p>
          <h2 className="font-display mt-5 text-4xl text-white md:text-5xl">立即訂購</h2>
          <div className="mx-auto mt-6 h-px w-16 bg-white/30" />
          <p className="font-serif-tc mt-6 text-[13px] leading-7 text-stone-400 md:text-sm md:leading-8">
            請完成匯款後填寫以下資料，我們將依照付款順序安排出貨。
          </p>
        </div>

        <form
          ref={formRef}
          className="mt-14 border border-white/10 bg-[#0b0b0b] px-6 py-10 md:px-10 md:py-14"
          onSubmit={handleSubmit}
        >
          {/* 01 購買商品數量 */}
          <SectionTitle index="01">購買商品數量</SectionTitle>
          <div className="mt-8 flex flex-col gap-6 border-b border-white/10 pb-8 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-serif-tc text-base text-white md:text-lg">
              Wild Thing 〈Free Man〉
              <span className="mx-2 text-white/25">·</span>
              <span className="text-stone-300">{SITE.priceLabel}</span>
            </p>
            <div className="flex items-center gap-5">
              <button
                type="button"
                aria-label="減少數量"
className="flex h-10 w-10 items-center justify-center border border-white/20 text-stone-300 transition-colors duration-300 hover:border-white hover:text-white focus-visible:border-white focus-visible:outline-none"
                onClick={() => setQty((prev) => Math.max(1, prev - 1))}
              >
                <Minus className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              <span className="font-display w-8 text-center text-xl text-white tabular-nums">{qty}</span>
              <button
                type="button"
                aria-label="增加數量"
className="flex h-10 w-10 items-center justify-center border border-white/20 text-stone-300 transition-colors duration-300 hover:border-white hover:text-white focus-visible:border-white focus-visible:outline-none"
                onClick={() => setQty((prev) => prev + 1)}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          </div>
          <p className="font-sans-ui mt-4 text-[11px] leading-6 tracking-[0.04em] text-stone-500">
            ※ 售價 NT$1,580 已含 7-ELEVEN 店到店運費，無須另加。
          </p>

          {/* 02 訂購人資料 */}
          <SectionTitle index="02">訂購人資料</SectionTitle>
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            <Field label="姓名">
              <input required type="text" name="name" autoComplete="name" className={inputClass} />
            </Field>
            <Field label="手機號碼">
              <input required type="tel" name="phone" autoComplete="tel" className={inputClass} />
            </Field>
            <div className="md:col-span-2">
              <Field label="Email">
                <input required type="email" name="email" autoComplete="email" className={inputClass} />
              </Field>
            </div>
          </div>

          {/* 03 7-ELEVEN 店到店 */}
          <SectionTitle index="03">7-ELEVEN 店到店資訊</SectionTitle>
          <a
            href="https://emap.pcsc.com.tw/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-sans-ui mt-6 inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[12px] tracking-[0.08em] text-stone-200 transition-colors duration-300 hover:border-white/60 hover:text-white focus-visible:border-white focus-visible:outline-none"
          >
            👉 點此查詢 7-ELEVEN 門市名稱與店號
          </a>
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            <Field label="7-ELEVEN 門市名稱 (必填)">
              <input required type="text" name="storeName" className={inputClass} />
            </Field>
            <Field label="7-ELEVEN 門市地址 (必填)">
              <input required type="text" name="storeAddress" className={inputClass} />
            </Field>
          </div>

          {/* 04 匯款資訊 */}
          <SectionTitle index="04">匯款資訊</SectionTitle>
          <div className="mt-8 divide-y divide-white/10 border border-white/10">
            {BANK.map((item) => (
              <div key={item.label} className="flex items-center justify-between gap-4 px-5 py-5">
                <div className="flex flex-col gap-1.5">
                  <span className="font-sans-ui text-[11px] tracking-[0.22em] text-stone-500">{item.label}</span>
                  <span className="font-serif-tc text-base tracking-wide text-white md:text-lg">{item.value}</span>
                </div>
                <button
                  type="button"
                  aria-label={`複製${item.label}`}
                  className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/15 text-stone-500 transition-colors duration-300 hover:border-white hover:text-white focus-visible:border-white focus-visible:outline-none"
                  onClick={() => copyValue(item.value, item.label)}
                >
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
          <p className="font-sans-ui mt-3 h-5 text-center text-[11px] tracking-[0.18em] text-stone-400">
            {copied ? `已複製${copied}` : ""}
          </p>
          <div className="mt-2 border-l-2 border-white/40 bg-white/[0.05] px-5 py-4">
            <p className="font-serif-tc text-[13px] leading-7 text-stone-300 md:text-sm">
              ⚠️ 溫馨提示：轉帳時請勿填寫「備註」，以利專人為您快速核對帳款與安排出貨。
            </p>
          </div>

          {/* 05 匯款確認 */}
          <SectionTitle index="05">匯款確認</SectionTitle>
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            <Field label="匯款日期">
              <input required type="date" name="transferDate" className={`${inputClass} [color-scheme:dark]`} />
            </Field>
            <Field label="匯款金額">
              <input required type="number" min="0" inputMode="numeric" name="transferAmount" className={inputClass} />
            </Field>
            <div className="md:col-span-2">
              <Field label="匯款帳號末五碼">
                <input
                  required
                  type="text"
                  maxLength={5}
                  inputMode="numeric"
                  name="accountLastFive"
                  className={inputClass}
                />
              </Field>
            </div>
          </div>

          {/* 06 備註 */}
          <SectionTitle index="06">備註</SectionTitle>
          <div className="mt-8">
            <Field label="其他備註（選填）">
              <textarea rows={4} name="note" className={`resize-none ${inputClass}`} />
            </Field>
          </div>

          {/* 底部提交 */}
          <div className="mt-14 border-t border-white/10 pt-8">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="font-sans-ui text-[10px] tracking-[0.28em] text-stone-500">訂購金額</p>
                <p className="font-display mt-2 text-3xl text-white tabular-nums">
                  NT$ {orderTotal.toLocaleString("zh-TW")}
                </p>
              </div>
              <button
                type="submit"
                disabled={qty < 1 || submitting}
                className="font-sans-ui w-full bg-white px-10 py-4 text-sm font-bold tracking-[0.28em] text-black transition-colors duration-300 hover:bg-stone-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black disabled:cursor-not-allowed disabled:bg-white/15 disabled:text-stone-500 sm:w-auto"
              >
                {submitting ? "送出中…" : "確認送出訂單"}
              </button>
            </div>
            <p className="font-sans-ui mt-4 text-[10px] tracking-[0.16em] text-stone-600">
              送出前請確認所有資料正確無誤
            </p>
          </div>
        </form>
      </div>
    </section>
  )
}

export default Order
